import { SVGProps } from 'react'

import { stegaClean } from '@sanity/client/stega'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { cn } from '@/app/utils/style'
import { icons, type LocalIconName } from '@/icons'
import { type FontAwesomeStyle, getFontAwesomeIcon } from '@/lib/fontawesome-generated'
import { Icon as SanityIcon } from '@/sanity.types'
import { DeepRequiredNullable } from '@/types/utils'

type IconWithQueryProjection = DeepRequiredNullable<SanityIcon>

type BaseSVGProps = Omit<SVGProps<SVGSVGElement>, 'width' | 'height'>

type PreIconProps =
  | {
      name: LocalIconName
      provider?: 'local'
      icon?: never
    }
  | {
      name: string
      provider: 'fontawesome'
      style?: FontAwesomeStyle
      icon?: never
    }
  | {
      name?: never
      provider?: null
      style?: never
      icon: IconWithQueryProjection
    }

export type IconProps = PreIconProps &
  BaseSVGProps & {
    size?: number
  }

function isIconObject(
  props: IconProps,
): props is IconProps & { icon: IconWithQueryProjection; name?: never } {
  return 'icon' in props && props.icon !== undefined
}

export const Icon = (props: IconProps) => {
  const { size = 24, className = '', ...rest } = props

  if (isIconObject(props)) {
    const { icon } = props

    // Clean stega encoding from entire Sanity object
    const cleanIcon = stegaClean(icon)

    const iconName = cleanIcon.name || ''
    const provider = (cleanIcon.provider as 'local' | 'fontawesome' | undefined) || 'local'
    const style = (cleanIcon.style as FontAwesomeStyle | undefined) || 'solid'

    if (!iconName) {
      console.warn('Icon component received empty icon name after cleaning')
      return null
    }

    if (provider === 'fontawesome') {
      const faIcon = getFontAwesomeIcon(iconName, style)
      if (!faIcon) {
        return null
      }
      return (
        <FontAwesomeIcon
          icon={faIcon}
          style={{ height: size, width: size }}
          className={cn('object-contain', className)}
        />
      )
    }

    const IconComponent = icons[iconName as LocalIconName]
    if (!IconComponent) {
      return null
    }
    return (
      <IconComponent
        height={size}
        preserveAspectRatio="xMidYMid meet"
        className={cn('w-auto', className)}
        {...rest}
      />
    )
  }

  const provider = props.provider || 'local'
  const iconName = props.name

  const cleanIconName = stegaClean(iconName) || iconName

  if (provider === 'fontawesome') {
    const style = (props.style as FontAwesomeStyle) || 'solid'
    const faIcon = getFontAwesomeIcon(cleanIconName, style)
    if (!faIcon) {
      return null
    }
    return (
      <FontAwesomeIcon
        icon={faIcon}
        style={{ height: size, width: size }}
        className={cn('object-contain', className)}
      />
    )
  }

  const IconComponent = icons[cleanIconName as LocalIconName]
  if (!IconComponent) {
    return null
  }
  return (
    <IconComponent
      height={size}
      preserveAspectRatio="xMidYMid meet"
      className={cn('w-auto', className)}
      {...rest}
    />
  )
}
